import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { auth } from '../config/firebase';
import { fetchRuntimeConfig } from '../lib/runtimeConfig';
import './FormPage.css';
import './ManageBilling.css';

let stripePromise: Promise<Stripe | null> | null = null;
function getStripePromise(pk: string) {
  if (!stripePromise) stripePromise = loadStripe(pk);
  return stripePromise;
}

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */
interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

interface SubscriptionInfo {
  id: string;
  status: string;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: number;
  trialEnd: number | null;
  plan: { amount: number; currency: string; interval: string } | null;
}

interface Invoice {
  id: string;
  number: string | null;
  date: number;
  amount: number;
  currency: string;
  status: string | null;
  pdfUrl: string | null;
}

/* ------------------------------------------------------------------ */
/*  Update Payment Form (rendered inside <Elements>)                  */
/* ------------------------------------------------------------------ */
function UpdatePaymentForm({ onSuccess, onCancel }: { onSuccess: () => void; onCancel: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError('');

    const { error: submitErr } = await elements.submit();
    if (submitErr) {
      setError(submitErr.message ?? 'Validation error');
      setProcessing(false);
      return;
    }

    const { error: confirmErr, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
    });

    if (confirmErr) {
      setError(confirmErr.message ?? 'Failed to update card');
      setProcessing(false);
      return;
    }

    // Set the new payment method as default on the backend
    if (setupIntent?.payment_method) {
      try {
        const API_BASE = (import.meta as any).env?.VITE_API_BASE || '';
        const idToken = await auth?.currentUser?.getIdToken();
        await fetch(`${API_BASE}/subscription/set-default-payment-method`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({ paymentMethodId: setupIntent.payment_method }),
        });
      } catch { /* webhook or next load will reconcile */ }
    }

    setProcessing(false);
    onSuccess();
  };

  return (
    <form onSubmit={handleSubmit} className="billing-update-form">
      <PaymentElement />
      {error && <div className="billing-error">{error}</div>}
      <div className="billing-form-actions">
        <button type="submit" className="btn btn-primary" disabled={!stripe || processing}>
          {processing ? 'Saving…' : 'Save Card'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={processing}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Page                                                         */
/* ------------------------------------------------------------------ */
export default function ManageBilling() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [error, setError] = useState('');
  const [actionMsg, setActionMsg] = useState('');

  // Update‐card flow state
  const [showCardForm, setShowCardForm] = useState(false);
  const [setupClientSecret, setSetupClientSecret] = useState<string | null>(null);
  const [publishableKey, setPublishableKey] = useState<string | null>(null);

  // Cancel confirmation
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const fetchBilling = useCallback(async () => {
    try {
      const API_BASE = (import.meta as any).env?.VITE_API_BASE || '';
      const idToken = await auth?.currentUser?.getIdToken();
      if (!idToken) { setError('Not authenticated'); setLoading(false); return; }
      const headers = { Authorization: `Bearer ${idToken}` };

      const [billingRes, invoicesRes] = await Promise.all([
        fetch(`${API_BASE}/subscription/billing`, { headers }),
        fetch(`${API_BASE}/subscription/invoices`, { headers }),
      ]);

      if (billingRes.ok) {
        const json = await billingRes.json();
        setSubscription(json.data?.subscription ?? null);
        setPaymentMethod(json.data?.paymentMethod ?? null);
      }
      if (invoicesRes.ok) {
        const json = await invoicesRes.json();
        setInvoices(json.data ?? []);
      }
    } catch (err) {
      console.error('Failed to load billing', err);
      setError('Failed to load billing information.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBilling(); }, [fetchBilling]);

  // Also fetch the Stripe publishable key for the card form
  useEffect(() => {
    (async () => {
      const cfg = await fetchRuntimeConfig();
      if (cfg?.stripePublishableKey) setPublishableKey(cfg.stripePublishableKey);
    })();
  }, []);

  const handleStartUpdateCard = async () => {
    setActionMsg('');
    try {
      const API_BASE = (import.meta as any).env?.VITE_API_BASE || '';
      const idToken = await auth?.currentUser?.getIdToken();
      const res = await fetch(`${API_BASE}/subscription/create-setup-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      });
      const json = await res.json();
      if (!res.ok) { setActionMsg(json?.error || 'Failed to start card update'); return; }
      setSetupClientSecret(json.data.clientSecret);
      setShowCardForm(true);
    } catch {
      setActionMsg('Failed to start card update.');
    }
  };

  const handleCardUpdateSuccess = () => {
    setShowCardForm(false);
    setSetupClientSecret(null);
    setActionMsg('Payment method updated successfully.');
    fetchBilling();
  };

  const handleCancel = async () => {
    setActionMsg('');
    try {
      const API_BASE = (import.meta as any).env?.VITE_API_BASE || '';
      const idToken = await auth?.currentUser?.getIdToken();
      const res = await fetch(`${API_BASE}/subscription/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        setActionMsg(json?.error || 'Failed to cancel');
        return;
      }
      setActionMsg('Your subscription will cancel at the end of the current billing period.');
      setShowCancelConfirm(false);
      fetchBilling();
    } catch {
      setActionMsg('Failed to cancel subscription.');
    }
  };

  const handleResume = async () => {
    setActionMsg('');
    try {
      const API_BASE = (import.meta as any).env?.VITE_API_BASE || '';
      const idToken = await auth?.currentUser?.getIdToken();
      const res = await fetch(`${API_BASE}/subscription/resume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        setActionMsg(json?.error || 'Failed to resume');
        return;
      }
      setActionMsg('Your subscription has been resumed.');
      fetchBilling();
    } catch {
      setActionMsg('Failed to resume subscription.');
    }
  };

  const formatDate = (ts: number) => new Date(ts * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  const formatAmount = (amount: number, currency: string) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(amount / 100);
  const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  if (loading) {
    return (
      <div className="form-page">
        <header className="page-header"><h1>Manage Billing</h1></header>
        <p style={{ textAlign: 'center' }}>Loading billing information…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="form-page">
        <header className="page-header"><h1>Manage Billing</h1></header>
        <div className="billing-error">{error}</div>
        <button className="btn btn-primary btn-full" onClick={() => navigate('/settings')}>Back to Settings</button>
      </div>
    );
  }

  const isActive = subscription && ['active', 'trialing'].includes(subscription.status);
  const isCanceling = subscription?.cancelAtPeriodEnd;

  return (
    <div className="form-page">
      <header className="page-header">
        <h1>Manage Billing</h1>
        <p>View and manage your subscription, payment method, and invoices</p>
      </header>

      {actionMsg && (
        <p className={`billing-action-msg ${actionMsg.includes('Failed') || actionMsg.includes('fail') ? 'error' : 'success'}`}>
          {actionMsg}
        </p>
      )}

      {/* ---- Subscription ---- */}
      <section className="settings-section card">
        <h2>Subscription</h2>
        {subscription ? (
          <div className="billing-details">
            <div className="billing-row">
              <span className="billing-label">Status</span>
              <span className={`billing-value billing-status ${isActive ? 'active' : 'inactive'}`}>
                {isCanceling ? 'Canceling' : capitalize(subscription.status)}
              </span>
            </div>
            {subscription.plan && (
              <div className="billing-row">
                <span className="billing-label">Plan</span>
                <span className="billing-value">
                  {formatAmount(subscription.plan.amount, subscription.plan.currency)} / {subscription.plan.interval}
                </span>
              </div>
            )}
            {subscription.trialEnd && subscription.status === 'trialing' && (
              <div className="billing-row">
                <span className="billing-label">Trial ends</span>
                <span className="billing-value">{formatDate(subscription.trialEnd)}</span>
              </div>
            )}
            <div className="billing-row">
              <span className="billing-label">{isCanceling ? 'Access until' : 'Next billing date'}</span>
              <span className="billing-value">{formatDate(subscription.currentPeriodEnd)}</span>
            </div>

            {isActive && !isCanceling && (
              <button type="button" className="btn btn-danger-outline btn-full billing-action-btn" onClick={() => setShowCancelConfirm(true)}>
                Cancel Subscription
              </button>
            )}
            {isCanceling && (
              <button type="button" className="btn btn-primary btn-full billing-action-btn" onClick={handleResume}>
                Resume Subscription
              </button>
            )}
            {!isActive && !isCanceling && (
              <button type="button" className="btn btn-primary btn-full billing-action-btn" onClick={() => navigate('/checkout?mode=reactivate')}>
                Reactivate Subscription
              </button>
            )}
          </div>
        ) : (
          <div>
            <p className="billing-empty">No active subscription.</p>
            <button type="button" className="btn btn-primary btn-full billing-action-btn" onClick={() => navigate('/checkout?mode=reactivate')}>
              Subscribe
            </button>
          </div>
        )}
      </section>

      {/* Cancel confirmation */}
      {showCancelConfirm && (
        <section className="settings-section card billing-confirm">
          <h2>Confirm Cancellation</h2>
          <p>Your subscription will remain active until the end of your current billing period. You won't be charged again.</p>
          <div className="billing-form-actions">
            <button type="button" className="btn btn-danger" onClick={handleCancel}>Yes, Cancel</button>
            <button type="button" className="btn btn-secondary" onClick={() => setShowCancelConfirm(false)}>Keep Subscription</button>
          </div>
        </section>
      )}

      {/* ---- Payment Method ---- */}
      <section className="settings-section card">
        <h2>Payment Method</h2>
        {paymentMethod && !showCardForm ? (
          <div className="billing-details">
            <div className="billing-row">
              <span className="billing-label">Card</span>
              <span className="billing-value">
                {capitalize(paymentMethod.brand)} •••• {paymentMethod.last4}
              </span>
            </div>
            <div className="billing-row">
              <span className="billing-label">Expires</span>
              <span className="billing-value">{String(paymentMethod.expMonth).padStart(2, '0')}/{paymentMethod.expYear}</span>
            </div>
            <button type="button" className="btn btn-secondary btn-full billing-action-btn" onClick={handleStartUpdateCard}>
              Update Card
            </button>
          </div>
        ) : showCardForm && setupClientSecret && publishableKey ? (
          <Elements
            stripe={getStripePromise(publishableKey)}
            options={{
              clientSecret: setupClientSecret,
              appearance: { theme: 'stripe', variables: { colorPrimary: '#43A047' } },
            }}
          >
            <UpdatePaymentForm
              onSuccess={handleCardUpdateSuccess}
              onCancel={() => { setShowCardForm(false); setSetupClientSecret(null); }}
            />
          </Elements>
        ) : !showCardForm ? (
          <div>
            <p className="billing-empty">No payment method on file.</p>
            <button type="button" className="btn btn-secondary btn-full billing-action-btn" onClick={handleStartUpdateCard}>
              Add Card
            </button>
          </div>
        ) : (
          <p style={{ textAlign: 'center' }}>Loading card form…</p>
        )}
      </section>

      {/* ---- Invoices ---- */}
      <section className="settings-section card">
        <h2>Invoices</h2>
        {invoices.length > 0 ? (
          <div className="billing-invoices">
            {invoices.map(inv => (
              <div key={inv.id} className="billing-invoice-row">
                <div className="invoice-info">
                  <span className="invoice-date">{formatDate(inv.date)}</span>
                  <span className="invoice-amount">{formatAmount(inv.amount, inv.currency)}</span>
                  <span className={`invoice-status ${inv.status === 'paid' ? 'paid' : ''}`}>
                    {inv.status ? capitalize(inv.status) : '—'}
                  </span>
                </div>
                {inv.pdfUrl && (
                  <a href={inv.pdfUrl} target="_blank" rel="noopener noreferrer" className="invoice-download">
                    PDF
                  </a>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="billing-empty">No invoices yet.</p>
        )}
      </section>

      <button type="button" className="btn btn-secondary btn-full" style={{ marginTop: 'var(--spacing-md)' }} onClick={() => navigate('/settings')}>
        ← Back to Settings
      </button>
    </div>
  );
}
